import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicApi, usersApi } from '../services/api';
import logo from '../images/american_pub_poker.jpg';
import './Home.css';

function distMi(lat1, lon1, lat2, lon2) {
  const toRad = (x) => x * Math.PI / 180;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function Home() {
  const [games, setGames] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const [closestGame, setClosestGame] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [signupSaving, setSignupSaving] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [signupError, setSignupError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scheduleRes, venuesRes] = await Promise.all([
        publicApi.getSchedule(),
        publicApi.getVenues(),
      ]);
      setGames(scheduleRes.data?.games || []);
      setVenues(venuesRes.data?.venues || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Filter to today's games
  const today = new Date();
  const todayGames = games.filter(g => {
    const d = new Date(g.startTime);
    return d.toDateString() === today.toDateString();
  });

  const findClosestGame = () => {
    if (!navigator.geolocation) {
      alert('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        // Find closest upcoming game
        let closest = null;
        let minDist = Infinity;
        for (const g of games) {
          const v = venues.find(vv => vv.id === g.venue.id);
          if (!v?.latitude || !v?.longitude) continue;
          if (new Date(g.startTime) < new Date()) continue;
          const dist = distMi(pos.coords.latitude, pos.coords.longitude, parseFloat(v.latitude), parseFloat(v.longitude));
          if (dist < minDist) {
            minDist = dist;
            closest = { ...g, distance: dist, venue: { ...g.venue, address: v.address } };
          }
        }
        setClosestGame(closest);
      },
      (err) => alert('Could not get location: ' + err.message),
      { timeout: 10000 }
    );
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError(null);
    setSignupSaving(true);
    try {
      const digits = signupData.phoneNumber.replace(/\D/g, '');
      if (digits.length < 10) {
        setSignupError('Please enter a valid 10-digit phone number');
        setSignupSaving(false);
        return;
      }
      const res = await usersApi.create({
        firstName: signupData.firstName.trim(),
        lastName: signupData.lastName.trim(),
        phoneNumber: digits,
        password: digits.slice(-4),
      });
      if (res.success !== false) {
        setSignupSuccess(true);
        setSignupData({ firstName: '', lastName: '', phoneNumber: '' });
      } else {
        setSignupError(res.message || 'Signup failed');
      }
    } catch (err) {
      setSignupError(err.message || 'Signup failed');
    } finally {
      setSignupSaving(false);
    }
  };

  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="apk-home">
      {/* Top nav */}
      <nav className="apk-nav">
        <div className="apk-nav-inner">
          <div className="apk-brand">
            <img src={logo} alt="American Pub Poker" />
            <span>American Pub Poker</span>
          </div>
          <div className="apk-nav-links">
            <Link to="/schedule">Schedule</Link>
            <Link to="/" className="apk-nav-cta">Admin Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="apk-hero">
        <div className="apk-hero-suits">
          <span className="apk-suit s1">♠</span>
          <span className="apk-suit s2">♥</span>
          <span className="apk-suit s3">♦</span>
          <span className="apk-suit s4">♣</span>
        </div>
        <div className="apk-hero-inner">
          <div className="apk-hero-eyebrow">
            <span className="apk-line"></span>
            <span>DENVER'S FREE POKER LEAGUE</span>
          </div>
          <h1 className="apk-hero-title">
            Free Texas Hold'em<br />
            <em>Every Day, Every Week</em>
          </h1>
          <p className="apk-hero-sub">
            <strong>100% Free Poker</strong> at 11 venues across Denver. No entry fees, no buy-ins.
            TDA rules. Play for prizes, points, and a shot at the championship.
          </p>
          <div className="apk-hero-ctas">
            <Link to="/schedule" className="apk-btn-primary">View Schedule →</Link>
            <button className="apk-btn-secondary" onClick={findClosestGame}>📍 Find Closest Game</button>
            <button className="apk-btn-secondary" onClick={() => setShowSignup(true)}>Sign Up</button>
          </div>
          {closestGame && (
            <div style={{ marginTop: 24, padding: 20, background: 'rgba(212,168,67,0.1)', borderRadius: 12, border: '1px solid rgba(212,168,67,0.3)' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#d4a843', marginBottom: 8 }}>CLOSEST UPCOMING GAME</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{closestGame.venue.name}</div>
              <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                {new Date(closestGame.startTime).toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })}
                {' · '}{closestGame.distance.toFixed(1)} miles away
              </div>
              {closestGame.venue.address && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{closestGame.venue.address}</div>}
            </div>
          )}
          <div className="apk-hero-stats">
            <div><strong>11</strong><span>Venues</span></div>
            <div><strong>34+</strong><span>Games / Week</span></div>
            <div><strong>$0</strong><span>Entry Fee</span></div>
          </div>
        </div>
      </section>

      {/* Today's games */}
      <section className="apk-today">
        <div className="apk-section-header">
          <h2>Today's Games</h2>
          <span className="apk-badge">{todayGames.length} {todayGames.length === 1 ? 'game' : 'games'}</span>
        </div>
        {loading ? (
          <p className="apk-loading">Loading...</p>
        ) : todayGames.length === 0 ? (
          <p className="apk-empty">No games today. Check the <Link to="/schedule">full schedule</Link>.</p>
        ) : (
          <div className="apk-game-grid">
            {todayGames.map(g => (
              <div key={g.id} className="apk-game-card">
                <div className="apk-game-time">{formatTime(g.startTime)}</div>
                <div className="apk-game-venue">{g.venue.name}</div>
                <div className="apk-game-type">{g.gameType}</div>
                {g.leagueName && <div className="apk-game-league">{g.leagueName}</div>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Venue showcase */}
      <section className="apk-venues">
        <div className="apk-section-header">
          <h2>Our Venues</h2>
          <Link to="/schedule" className="apk-link">See All →</Link>
        </div>
        <div className="apk-venue-grid">
          {venues.slice(0, 8).map(v => (
            <div key={v.id} className="apk-venue-card">
              <div className="apk-venue-name">{v.name}</div>
              {v.address && <div className="apk-venue-address">{v.address}</div>}
              {v.rating && (
                <div className="apk-venue-rating">
                  ⭐ {v.rating} {v.ratingCount > 0 && <span>({v.ratingCount})</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Signup Modal */}
      {showSignup && (
        <div onClick={() => setShowSignup(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0a1628', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 32, maxWidth: 460, width: '100%' }}>
            <h2 style={{ marginTop: 0, fontSize: 28, fontWeight: 900 }}>Join American Pub Poker</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>Create your free player account to start tracking your wins.</p>
            {signupSuccess ? (
              <div>
                <div style={{ fontSize: 64, textAlign: 'center', marginBottom: 16 }}>🎉</div>
                <h3 style={{ textAlign: 'center', color: '#2ecc71' }}>Welcome to American Pub Poker!</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', marginBottom: 24 }}>
                  Your account is created. Show your phone number at any venue to join games. Default password: <strong>1234</strong>
                </p>
                <button onClick={() => { setShowSignup(false); setSignupSuccess(false); }} className="apk-btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleSignup}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.7)' }}>FIRST NAME</label>
                  <input
                    type="text"
                    value={signupData.firstName}
                    onChange={e => setSignupData({...signupData, firstName: e.target.value})}
                    required
                    style={{ width: '100%', padding: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff', fontSize: 16 }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.7)' }}>LAST NAME</label>
                  <input
                    type="text"
                    value={signupData.lastName}
                    onChange={e => setSignupData({...signupData, lastName: e.target.value})}
                    required
                    style={{ width: '100%', padding: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff', fontSize: 16 }}
                  />
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'rgba(255,255,255,0.7)' }}>PHONE NUMBER</label>
                  <input
                    type="tel"
                    placeholder="(303) 555-1234"
                    value={signupData.phoneNumber}
                    onChange={e => setSignupData({...signupData, phoneNumber: e.target.value})}
                    required
                    style={{ width: '100%', padding: 14, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: '#fff', fontSize: 16 }}
                  />
                </div>
                {signupError && <div style={{ color: '#ff6b5e', marginBottom: 16, fontSize: 14 }}>{signupError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="button" onClick={() => setShowSignup(false)} className="apk-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
                  <button type="submit" className="apk-btn-primary" disabled={signupSaving} style={{ flex: 1, justifyContent: 'center' }}>
                    {signupSaving ? 'Creating...' : 'Sign Up'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="apk-footer">
        <div className="apk-footer-inner">
          <img src={logo} alt="American Pub Poker" />
          <p>American Pub Poker — Denver's Free Poker League</p>
          <div className="apk-footer-links">
            <Link to="/schedule">Schedule</Link>
            <a href="https://facebook.com">Facebook</a>
            <Link to="/">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
